<template>
  <div>
    <n-search-container :fields="searchFields" @search="search" @reset="reset">
      <n-search-input ref="search" v-model="params" :fields="searchFields" />
    </n-search-container>

    <b-card no-body class="mb-0 py-2">
      <b-card-text class="mb-2 d-flex justify-content-between">
        <b-col>
          <label>{{ $t('general.entries') }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          >
            <template #no-options>
              {{ $t('selection.noOption') }}
            </template>
          </v-select>
        </b-col>
        <b-button
          v-if="
            !$can('manage', manageRemotePermission) &&
            $can('manage', 'manage-service-product')
          "
          class="mr-1"
          :style="{ marginTop: '10px' }"
          variant="primary"
          size="sm"
          @click="openModal()"
        >
          {{ $t('button.add') }}
        </b-button>
      </b-card-text>

      <!-- Types Table -->
      <n-table
        ref="table"
        :fields="fields"
        :items="products"
        :busy="loading"
        :current-page="params.page"
        :per-page="perPage"
        :total="total"
        :deletable="true"
        :updatable="true"
        :type="permissionType"
        :route="route"
        @sort-changed="sortChanged"
        @destroy="handleDelete"
      >
        <template #cell(name)="data">
          {{ getNameByLang(data.item) }}
        </template>
        <template #cell(price)="data">
          {{ data.item.price || '-' }}
        </template>
        <template #cell(currency)="data">
          {{ data.item.currency || '-' }}
        </template>
        <template #cell(serviceCategoryCode)="data">
          {{ data.item.serviceCategoryCode || '-' }}
        </template>
        <template #cell(serviceTypeCode)="data">
          {{ data.item.serviceTypeCode || '-' }}
        </template>
        <template #cell(photo)="data">
          <img
            v-if="data.item.photo"
            :src="data.item.photo"
            alt="Icon"
            style="
              width: 40px;
              height: 40px;
              object-fit: cover;
              border-radius: 8px;
            "
          />
        </template>
        <template #cell(description)="data">
          {{ data.item.description || '-' }}
        </template>
        <template #cell(isActive)="data">
          <b-badge v-if="data.item.isActive" pill variant="success" badge-glow>
            {{ $t(`status.active`) }}
          </b-badge>
          <b-badge v-else pill variant="danger" badge-glow>
            {{ $t(`status.inactive`) }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <template
              v-if="
                !$can('manage', manageRemotePermission) &&
                $can('manage', 'manage-service-product')
              "
            >
              <b-button
                v-if="$can('update', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.update')"
                variant="primary"
                pill
                size="sm"
                @click="openModal(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="$can('delete', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.delete')"
                variant="danger"
                pill
                size="sm"
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </template>
            <b-button
              v-else
              v-b-tooltip.hover
              :title="$t('general.not_allow_to_modify')"
              variant="secondary"
              pill
              size="sm"
            >
              <feather-icon icon="LockIcon" />
            </b-button>
          </div>
        </template>
      </n-table>

      <!-- Pagination -->
      <n-pagination
        class="mt-2"
        :total="total"
        :per-page="perPage"
        :page="params.page"
        @change="onPageChange"
      />

      <!-- Type Modal -->
      <b-modal
        v-model="showModal"
        :title="
          modalMode === 'add'
            ? $t('button.addProduct')
            : $t('button.editProduct')
        "
        size="lg"
        hide-footer
        @hidden="resetForm"
      >
        <b-form>
          <validation-observer ref="createForm" v-slot="{ invalid }">
            <!-- Name Field with Language Support -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.name')"
              :rules="
                locale === $i18n.locale.split('-')[0]
                  ? 'required|min:3|max:47'
                  : 'min:3|max:47'
              "
            >
              <b-form-group :label="$t('field.name')">
                <b-row>
                  <b-col>
                    <b-form-input
                      v-model="form.name[locale]"
                      :placeholder="$t('placeholder.enterTitle')"
                      :state="errors.length > 0 ? false : null"
                    />
                  </b-col>
                  <b-col
                    cols="1"
                    class="d-flex align-items-center justify-content-end"
                  >
                    <b-form-select
                      v-model="locale"
                      :options="locales"
                      :style="{
                        minWidth: '65px',
                        width: 'auto',
                        maxWidth: '120px'
                      }"
                    />
                  </b-col>
                </b-row>
                <small class="text-danger" :style="{ paddingTop: '5px' }">
                  {{ formatErrorMessage(errors[0]) }}
                </small>
              </b-form-group>
            </validation-provider>

            <!--price Field -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.price')"
              rules="required|decimal:2|min_value:0"
            >
              <b-form-group :label="$t('field.price')">
                <b-form-input
                  v-model="form.price"
                  type="number"
                  min="0"
                  step="0.01"
                  @input="formatPrice"
                  :placeholder="$t('placeholder.enterPrice')"
                  :state="errors.length > 0 ? false : null"
                  @keypress="isNumber($event)"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.currency')"
              rules="required"
            >
              <b-form-group :label="$t('field.currency')">
                <n-single-select
                  v-model="form.currency"
                  :options="[
                    { value: 'CNY', name: 'CNY' },
                    { value: 'USD', name: 'USD' },
                    { value: 'KHR', name: 'KHR' },
                    { value: 'VND', name: 'VND' }
                  ]"
                  selection-key="value"
                  selection-label="name"
                  :reduce="true"
                />
              </b-form-group>
            </validation-provider>

            <!-- Service Category Code Field -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.categoryCode')"
              rules="required"
            >
              <b-form-group :label="$t('field.categoryCode')">
                <n-single-select
                  v-model="form.serviceCategoryCode"
                  :options="categoryOptions"
                  selection-label="text"
                  selection-key="value"
                  :placeholder="$t('placeholder.selectCategory')"
                  :state="errors.length > 0 ? false : null"
                  :is-translate-label-value="false"
                  :reduce="true"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Service Type Code Field -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.typeCode')"
              rules="required"
            >
              <b-form-group :label="$t('field.typeCode')">
                <n-async-single-select
                  :key="form.serviceCategoryCode"
                  v-model="form.serviceTypeCode"
                  repository="serviceType"
                  selection-key="code"
                  clearable
                  :placeholder="$t('placeholder.selectType')"
                  selection-label="translations"
                  :state="errors.length > 0 ? 'is-invalid' : null"
                  :init-options="form.serviceTypeCode"
                  :immediate="true"
                  :always-fresh="true"
                  :must-excluded-ids="[]"
                  :query="{
                    categoryCode: form.serviceCategoryCode
                  }"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Icon Field -->
            <validation-provider v-slot="{ errors }" :name="$t('field.icon')">
              <b-form-group :label="$t('field.icon')">
                <n-single-image-uploader
                  v-model="form.image.icon"
                  :pw="150"
                  :ph="150"
                  :full-width="false"
                  :image="form.photo"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!--Code Field -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.code')"
              rules="required|min:2|max:50"
            >
              <b-form-group :label="$t('field.code')">
                <b-form-input
                  v-model="form.code"
                  :disabled="modalMode === 'edit'"
                  :placeholder="$t('placeholder.enterCode')"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Description Field -->
            <b-form-group
              :label="$t('field.description')"
              label-for="description"
            >
              <b-form-textarea
                id="description"
                v-model="form.description"
                :placeholder="$t('placeholder.enterDescription')"
                rows="3"
              />
            </b-form-group>

            <!-- Status Field -->
            <validation-provider
              v-if="modalMode === 'edit'"
              v-slot="{ errors }"
              :name="$t('field.status')"
              rules="required"
            >
              <b-form-group :label="$t('field.status')" label-for="isActive">
                <b-form-radio-group
                  id="isActive"
                  v-model="form.isActive"
                  :options="[
                    { text: $t('status.active'), value: true },
                    { text: $t('status.inactive'), value: false }
                  ]"
                  buttons
                  button-variant="outline-primary rounded-0"
                  class="flex-wrap"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <div class="d-flex justify-content-end">
              <n-button-loading
                variant="primary"
                :loading="submitLoading"
                :disabled="
                  invalid ||
                  !form.name[locale] ||
                  !form.serviceCategoryCode ||
                  !form.serviceTypeCode ||
                  !form.code
                "
                @submit="handleSubmit"
              >
                {{ $t('button.save') }}
              </n-button-loading>
            </div>
          </validation-observer>
        </b-form>

        <template #modal-footer />
      </b-modal>
    </b-card>
  </div>
</template>

<script>
  import {
    BCard,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormRadioGroup,
    BCardText,
    BCol,
    BRow,
    BBadge
  } from 'bootstrap-vue';
  import { ValidationProvider, ValidationObserver } from 'vee-validate';
  import vSelect from 'vue-select';
  import NTable from '@/components/NTable.vue';
  import NPagination from '@/components/NPagination.vue';
  import NSearchContainer from '@/components/NSearchContainer.vue';
  import NSearchInput from '@/components/NSearchInput.vue';
  import Repository from '@/repositories/RepositoryFactory';
  import NSingleSelect from '@/components/NSingleSelect.vue';
  import NAsyncSingleSelect from '@/components/NAsyncSingleSelect';
  import NButtonLoading from '@/components/NButtonLoading.vue';
  import NSingleImageUploader from '@/components/NSingleImageUploader.vue';

  const ServiceSystemRepository = Repository.get('serviceSystem');
  const FileRepository = Repository.get('file');

  export default {
    name: 'ServiceType',
    components: {
      BCard,
      BButton,
      BModal,
      BForm,
      BFormGroup,
      BFormInput,
      BFormTextarea,
      BFormSelect,
      BFormRadioGroup,
      BCardText,
      BCol,
      BRow,
      vSelect,
      NTable,
      NPagination,
      NSearchContainer,
      NSearchInput,
      ValidationProvider,
      ValidationObserver,
      NSingleSelect,
      NAsyncSingleSelect,
      BBadge,
      NButtonLoading,
      NSingleImageUploader
    },
    data() {
      return {
        products: [],
        types: [],
        categories: [],
        fields: [
          {
            key: 'name',
            label: 'field.name',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'price',
            label: 'field.price',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'currency',
            label: 'field.currency',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'code',
            label: 'field.code',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'photo',
            label: 'field.photo',
            sortable: false,
            thClass: 'w-50px'
          },

          {
            key: 'serviceCategoryCode',
            label: 'field.categoryCode',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'serviceTypeCode',
            label: 'field.typeCode',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'description',
            label: 'field.description',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'isActive',
            label: 'field.status',
            sortable: true,
            thClass: 'w-150px'
          },
          {
            key: 'actions',
            label: 'general.action'
          }
        ],
        searchFields: [
          {
            key: 'name',
            label: 'field.name',
            type: 'text',
            md: 6,
            lg: 6
          }
        ],
        showModal: false,
        modalMode: 'add',
        form: {
          name: {
            en: '',
            zh: '',
            km: '',
            vi: ''
          },
          price: 0,
          currency: null,
          photo: null,
          image: {
            icon: null
          },
          serviceCategoryCode: '',
          serviceTypeCode: '',
          code: '',
          description: '',
          isActive: true
        },
        selectedType: null,
        loading: false,
        perPage: this.$store.state.pagination.perPage,
        perPageOptions: this.$store.state.pagination.perPageOptions,
        total: 0,
        params: {
          page: 1,
          name: null,
          order: null,
          sort: null
        },
        locale: this.$i18n.locale.split('-')[0],
        locales: [
          { value: 'en', text: 'English' },
          { value: 'zh', text: '中文' },
          { value: 'km', text: 'ខ្មែរ' },
          { value: 'vi', text: 'Tiếng Anh' }
        ],
        submitLoading: false,
        resetIndex: 0
      };
    },
    computed: {
      categoryOptions() {
        const options = this.categories.map(category => ({
          value: category.code,
          text: this.getNameByLang(category)
        }));
        console.log('options', options);
        return options;
      },
      typeOptions() {
        const options = this.types.map(type => ({
          value: type.code,
          text: this.getNameByLang(type)
        }));
        console.log('options', options);
        return options;
      }
    },
    watch: {
      '$i18n.locale': function (val) {
        const [locale] = val.split('-');
        this.locale = locale;
      },
      'form.code': function (val) {
        if (val) {
          this.form.code = val.toUpperCase().replace(/\s+/g, '_');
        }
      }
    },
    mounted() {
      this.fetchProducts();
      this.fetchCategories();
      this.fetchTypes();
      // this.fetchProducts();
    },
    methods: {
      isNumber(evt) {
        evt = evt || window.event;
        const charCode = evt.which ? evt.which : evt.keyCode;
        if (
          charCode > 31 &&
          (charCode < 48 || charCode > 57) &&
          charCode !== 46
        ) {
          evt.preventDefault();
        } else {
          return true;
        }
      },
      formatPrice(event) {
        // Ensure the value is a valid number and format it to 2 decimal places
        let value = event.target.value;
        if (value && value !== '') {
          // Remove any non-numeric characters except decimal point
          value = value.replace(/[^0-9.]/g, '');
          // Ensure only one decimal point
          const parts = value.split('.');
          if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
          }
          // Format to 2 decimal places
          const num = parseFloat(value);
          if (!isNaN(num)) {
            this.form.price = num.toFixed(2);
          }
        }
      },
      formatErrorMessage(message) {
        return message ? this.$t(message) : '';
      },
      getNameByLang(data) {
        if (!data.name) return '';

        if (this.$i18n.locale === 'vi') {
          return data.name.vi ? data.name.vi : data.name.en;
        }
        if (this.$i18n.locale === 'km') {
          return data.name.km ? data.name.km : data.name.en;
        }
        if (this.$i18n.locale === 'en') {
          return data.name.en ? data.name.en : data.name.zh;
        }
        return data.name.zh ? data.name.zh : data.name.en;
      },
      async fetchCategories() {
        try {
          const response = await ServiceSystemRepository.getServiceCategory({
            isActive: true,
            limit: 1000,
            page: 1
          });
          this.categories = response.data.data.list || response.data.data;
        } catch (error) {
          console.error(error);
        }
      },
      async fetchTypes() {
        try {
          const response = await ServiceSystemRepository.getServiceType({
            isActive: true,
            limit: 100,
            page: 1
          });
          this.types = response.data.data.list || response.data.data;
        } catch (error) {
          console.error(error);
        }
      },
      async fetchProducts() {
        try {
          this.loading = true;
          const response = await ServiceSystemRepository.getServiceProduct({
            ...this.params,
            perPage: this.perPage
          });
          this.products = response.data.data.list || response.data.data;
          this.total = response.data.data.total || this.products.length;
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      },
      openModal(type = null) {
        this.modalMode = type ? 'edit' : 'add';
        if (type) {
          this.selectedType = type;
          this.form = {
            name: {
              en: type.name?.en || '',
              zh: type.name?.zh || '',
              km: type.name?.km || '',
              vi: type.name?.vi || ''
            },
            photo: type.photo || null,
            image: {
              icon: null
            },
            price: type.price || 0,
            currency: type.currency || null,
            serviceCategoryCode: type.serviceCategoryCode || '',
            serviceTypeCode: type.serviceTypeCode || '',
            description: type.description || '',
            isActive: type.isActive ?? true,
            code: type.code || ''
          };
        }
        this.showModal = true;
      },
      // onIconChange(file) {
      //   this.form.image.icon = file
      // },
      async uploadImage(file) {
        const formData = new FormData();
        formData.append('image', file);
        return FileRepository.upload(formData);
      },
      async handleSubmit() {
        this.submitLoading = true;
        const isValid = await this.$refs.createForm.validate();
        if (!isValid) return;

        try {
          const payload = {
            name: this.form.name,
            serviceCategoryCode:
              typeof this.form.serviceCategoryCode === 'object'
                ? this.form.serviceCategoryCode.value
                : this.form.serviceCategoryCode,
            serviceTypeCode:
              typeof this.form.serviceTypeCode === 'object'
                ? this.form.serviceTypeCode.value
                : this.form.serviceTypeCode,
            description: this.form.description,
            price: parseFloat(this.form.price) || 0,
            currency: this.form.currency || null,
            photo: this.form.photo || null,
            code: this.form.code.trim().toUpperCase().replace(/\s+/g, '_')
          };

          // Handle icon upload if exists
          if (this.form.image.icon) {
            try {
              console.log('uploading icon', this.form.image.icon);
              const response = await this.uploadImage(this.form.image.icon);
              payload.photo = response?.data?.data?.url;
            } catch (error) {
              console.error('Failed to upload icon:', error);
              this.submitLoading = false;
              return;
            }
          }

          if (this.modalMode === 'edit') {
            payload.isActive = this.form.isActive;
          }

          if (this.modalMode === 'add') {
            await ServiceSystemRepository.createServiceProduct(payload);
          } else {
            await ServiceSystemRepository.updateServiceProduct(
              this.selectedType.id,
              payload
            );
          }
          this.showModal = false;
          this.fetchProducts();
        } catch (error) {
          console.error(error);
        } finally {
          this.submitLoading = false;
        }
      },
      async handleDelete(id) {
        try {
          await ServiceSystemRepository.deleteServiceProduct(id);
          this.fetchProducts();
        } catch (error) {
          console.error(error);
        }
      },
      resetForm() {
        this.form = {
          name: {
            en: '',
            zh: '',
            km: '',
            vi: ''
          },
          photo: null,
          image: {
            icon: null
          },
          serviceCategoryCode: '',
          serviceTypeCode: '',
          description: '',
          price: 0,
          currency: null,
          photo: null,
          isActive: true,
          code: ''
        };
        this.selectedType = null;
      },
      onPageChange(page) {
        this.params.page = page;
        this.fetchProducts();
      },
      search() {
        this.params.page = 1;
        this.fetchProducts();
      },
      reset() {
        this.$refs.search.reset();
        this.params = {
          page: 1,
          name: null,
          order: null,
          sort: null
        };
        this.fetchProducts();
      },
      updateQueryString() {
        this.$router.replace({ query: this.params });
      },
      sortChanged(sortBy, sortDirection) {
        this.params.order = sortBy;
        this.params.sort = sortDirection;
        this.updateQueryString();
        this.fetchProducts();
      },
      confirmDelete(id) {
        this.$bvModal
          .msgBoxConfirm(
            this.$t('alert.unableToRevertAfterDelete', {
              item:
                this.getNameByLang(
                  this.products.find(item => item.id === id)
                ) || ''
            }),
            {
              title: `${this.$t('button.confirm')}!`,
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'primary',
              okTitle: this.$t('button.delete'),
              cancelTitle: this.$t('button.cancel'),
              cancelVariant: 'outline-secondary',
              hideHeaderClose: false,
              centered: true
            }
          )
          .then(value => {
            if (value) {
              this.handleDelete(id);
            }
          });
      }
    },
    setup() {
      // const permissionType = 'service-type'
      const permissionType = 'manage-users';
      const route = 'service-product';
      const manageRemotePermission = 'manage-remote-service-product';

      return {
        permissionType,
        route,
        manageRemotePermission
      };
    }
  };
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';

  .per-page-selector {
    width: 90px;
  }
</style>
